<template>
  <bm-barcode-camera
    v-if="currentInputMode === BmBarcodeTextMode.Scanner"
    :value.sync="internalValue"
    :disable-camera-input="disableCameraInput"
    :computed-rules="computedRules"
    :background-color="inputCompleted ? 'successPastel' : ''"
    :append-icon="inputCompleted ? bmIconName('Check') : ''"
    v-bind="$attrs"
    @keydown="onKey"
    @update:value="bubbleInput"
    @valid-scanned="inputComplete($event, false)"
  >
    <template v-if="showInputToggle" #append-outer>
      <bm-btn button-type="secondary" icon @click="changeInputType">
        <bm-icon icon="PhotoCamera" />
      </bm-btn>
    </template>
    <template #scanner>
      <slot name="scanner" />
    </template>
    <template #action>
      <slot name="action" />
    </template>
  </bm-barcode-camera>

  <bm-barcode-laser
    v-else-if="currentInputMode === BmBarcodeTextMode.Laser"
    v-bind="$attrs"
    @scan-complete="scanComplete"
  >
    <template v-if="showInputToggle" #append-outer>
      <bm-btn button-type="secondary" icon @click="changeInputType">
        <bm-icon icon="CropFree" />
      </bm-btn>
    </template>
    <template #action>
      <slot name="action" />
    </template>
  </bm-barcode-laser>

  <bm-barcode-text-input
    v-else
    :value.sync="internalValue"
    :computed-rules="computedRules"
    :background-color="inputCompleted ? 'successPastel' : ''"
    :append-icon="inputCompleted ? bmIconName('Check') : ''"
    v-bind="$attrs"
    @keydown="onKey"
    @update:value="bubbleInput"
  >
    <template v-if="showInputToggle" #append-outer>
      <bm-btn button-type="secondary" icon @click="changeInputType">
        <bm-icon icon="Keyboard" />
      </bm-btn>
    </template>
    <template #action>
      <slot name="action" />
    </template>
  </bm-barcode-text-input>
</template>

<script setup lang="ts">
import BmBtn from "@/components/controls/input/BmBtn.vue";
import BmBarcodeCamera from "@/components/controls/device/barcodes/BmBarcodeCamera.vue";
import BmBarcodeLaser from "@/components/controls/device/barcodes/BmBarcodeLaser.vue";
import BmBarcodeTextInput from "@/components/controls/device/barcodes/BmBarcodeTextInput.vue";
import type { ValidationRule } from "@/plugins/validation";
import { ruleSet } from "@/plugins/validation";
import { EnumBrewmanBarcodeType } from "@/app/services/generated/enums/enumBrewmanBarcodeType.generated";
import appStore from "@/store/modules/appStore";
import { EnumSnackbarType } from "@/components/controls/snackbar/SnackbarTypes";
import { translateWithParams } from "@/plugins/i18n";
import { barcodeFailure } from "@/assets/sounds/barcodeSounds";
import { validateBrewmanBarcode } from "@/plugins/bmBarcodeValidator";
import { makeRef } from "@/components/composables/reactivityTypes";
import {
  BmBarcodeTextMode,
  stripUrlPartsFromBarcode,
} from "@/components/controls/device/barcodeUtils";
import { computed, nextTick, watch } from "vue";
import { bmIconName } from "@/components/controls/bmIconMap";

export interface BmBarcodeInputProps {
  barcodeTypes?: readonly EnumBrewmanBarcodeType[];
  value?: string | null;
  lockedMode?: BmBarcodeTextMode | null;
  disableCameraInput?: boolean;
  rules?: readonly ValidationRule[];
}

interface BmBarcodeInputEvents {
  (event: "update:value", value: string): void;
  (event: "input-complete", value: string): void;
  (event: "valid-scanned", value: string): void;
}

const props = withDefaults(defineProps<BmBarcodeInputProps>(), {
  barcodeTypes: () => [EnumBrewmanBarcodeType.none],
  value: null,
  lockedMode: null,
  disableCameraInput: false,
  rules: () => [],
});

const emit = defineEmits<BmBarcodeInputEvents>();

const internalValue = makeRef(props.value ?? "");
const inputCompleted = makeRef(false);
const completeAnimationTimeoutRef = makeRef(0);

const currentInputMode = computed(() => props.lockedMode ?? inputMode.value);

const showInputToggle = computed(() => props.lockedMode === null);

const computedRules = computed((): ValidationRule[] => {
  const barcodeRule = ruleSet.isBrewmanBarcodeOfTypes([...props.barcodeTypes]);
  return barcodeRule ? [barcodeRule, ...props.rules] : [...props.rules];
});

const allModes = [
  BmBarcodeTextMode.Text,
  BmBarcodeTextMode.Laser,
  BmBarcodeTextMode.Scanner,
];

const storedPreference = localStorage
  .getItem("barcode-scanner-preference")
  ?.toLowerCase();

const inputMode = makeRef(
  allModes.find((mode) => mode.toString().toLowerCase() === storedPreference) ??
    allModes[0]
);

watch(
  () => props.value,
  (newValue) => {
    internalValue.value = newValue ?? "";
  },
  { immediate: true }
);

const changeInputType = () => {
  inputMode.value =
    allModes[allModes.indexOf(inputMode.value) + 1] ?? allModes[0];
  // Update device preference
  localStorage.setItem(
    "barcode-scanner-preference",
    inputMode.value.toString().toLowerCase()
  );
};

const bubbleInput = (newValue: string) => {
  internalValue.value = newValue;
  emit("update:value", newValue);
};

const onKey = (event: KeyboardEvent) => {
  if (event.key === "Enter") {
    inputComplete(stripUrlPartsFromBarcode(internalValue.value), true);
  }
};

const inputComplete = (barcode: string, fromScan: boolean = false) => {
  // we cannot wait for v-model update to propagate back to us (so we can update our children) as it's too slow. Form
  // validation will likely occur as a result of 'input-complete' and our controls need to see the most recent value.

  emit("update:value", barcode); // Update bound :value.sync

  nextTick(() => {
    // Required so input-complete is fired after the input event
    const errors = computedRules.value
      .map((rule) => rule(barcode))
      .filter((x) => typeof x === "string") as string[];

    if (errors.length) {
      appStore.pushSnackbarConfig({
        text: translateWithParams("scanner_errors", {
          errors: errors.join(", "),
          barcode,
        }),
        timeout: 2000,
        type: EnumSnackbarType.warning,
      });

      //Clear internal state
      internalValue.value = "";
      return;
    }

    emit("input-complete", barcode); // Inform that we have a full input (scan or manual)

    inputCompleted.value = true;
    clearTimeout(completeAnimationTimeoutRef.value);
    //("+" causes a toPrimative call with "number" hint)
    completeAnimationTimeoutRef.value = +setTimeout(() => {
      inputCompleted.value = false;
    }, 2000);

    // Store last action type to better preserve expected behaviours for users
    if (fromScan) {
      emit("valid-scanned", barcode); // In case anyone cares if it was scanned rather than entered.
    }

    internalValue.value = "";
  });
};

const playFailureSound = async () => {
  try {
    await new Audio(barcodeFailure).play();
  } catch (e) {
    // leave empty - no need to throw error/alert user
  }
};

/**
 * Strips url from barcode and passes to inputComplete
 */
const scanComplete = (barcode: string | null) => {
  if (barcode !== null) {
    const stripped = stripUrlPartsFromBarcode(barcode);

    // There is very inconsistent handling of barcode errors and rules handed down to some scanners not others
    // Laser scanner follows this path, and we were having problems where rubbish scans were not being
    // noticed, so here we will play a sound if the scan is probably not wanted.
    // Note that many scans will interpret as V6PermLabel, so for now we will bleep on those
    // (only possibly used during migration so not an ongoing problem)
    const type = validateBrewmanBarcode(stripped);
    if (type.isNone() || type.isV6PermLabel()) {
      // Initiate Failure Sound playing - async but deliberately do not wait for finish
      playFailureSound();
    }

    inputComplete(stripped, true);
  }
};
</script>
